import {
  ArrowDownIcon, ArrowUpIcon, CircleIcon,
  DuotoneCircleIcon, DuotoneArrowUpIcon, DuotoneArrowDownIcon,
} from "../icons";
import { FIELD_TYPES } from "../lib/constants";
import { getActiveIndicatorPalette, isColorBlindModeEnabled } from "../theme/colorBlindMode";
// DEV-838: Single source of truth for the HSE floor-cap rule. When a
// project row carries `hse_bundle`, Mission and Summary indicator colors
// are routed through this helper so KpiGridTable, KpiSummaryHeader, and
// any future surface always show the same color for the same data.
// DEV-851: Same contract for Data Reliability — getDataReliabilityDisplayState
// returns isComplete=true for closed projects so the cell renders blue.
import {
  getMissionDisplayState,
  getSummaryDisplayState,
  getDataReliabilityDisplayState,
} from "../Data/displayKpiState";

/**
 * Static fallback colors.  The `colors` enum is kept for non-indicator uses
 * (e.g. `getCompletedIndicator`).  For indicator rendering, prefer
 * `getActiveIndicatorPalette()` which respects color-blind mode.
 */
enum colors {
  'green'='#45BB47',
  'yellow'='#ffd13a',
  'red'='#D53541',
  'gray'='#657079',
  'black'='#000000',
  'completed'='#106DD5',
}

const defineIndicatorColor = (value, completed) => {
  // NOTE: UI spec uses discrete indicator_color_value codes:
  // 0=no-data (render as GREY), 1=red, 2=yellow, 3=green.
  // During local development we also support legacy/continuous values
  // by bucketing into the same 4 states.

  const palette = getActiveIndicatorPalette();

  // DEV-966. Completion override: when the caller signals this cell
  // represents a completed project, override the engine-computed color
  // with the palette's `completed` blue (colorblind-aware via palette).
  // Applies to project-leaf cells only — group rollups never pass
  // completed=true (group rows don't carry a project_status), so the
  // aggregated rollup color stays engine-derived. The natural trend
  // SHAPE is preserved by defineIndicatorIcon, so a completed project
  // still shows its up/down/flat direction; only the color changes to
  // signal "this project is final / locked."
  if (completed) return palette.completed;

  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return palette.grey;

  // Discrete codes (preferred)
  if (n === 0) return palette.grey;
  if (n === 1) return palette.red;
  if (n === 2) return palette.yellow;
  if (n === 3) return palette.green;

  // Legacy/continuous values (back-compat with existing fixture data)
  // We interpret floats as "near" a discrete code.
  // Midpoint bucketing makes 2.x -> yellow and 3.x -> green (more intuitive).
  if (n <= 0.5) return palette.grey;
  if (n <= 1.5) return palette.red;
  if (n <= 2.5) return palette.yellow;
  return palette.green;
}
/**
 * Select the correct icon component for a KPI indicator.
 *
 * In normal vision: renders the duotone halo variant (solid core + faint outer ring).
 * In color-blind mode: renders the plain solid variant (no halo) for maximum clarity.
 *
 * @param pulsing — when true, the icon is wrapped in an HTML <span> with the
 *                  `kpi-pulse-wrapper` CSS class that animates scale + glow.
 *                  This wrapper approach avoids SVG transform-origin issues.
 */
const defineIndicatorIcon = (color, trend, completed, size, pulsing = false) => {
  let h, w;
  switch (size) {
    case 'sm':
      h = 28;
      w = 28;
      break;
    case 'lg':
      h = 32;
      w = 32;
      break;
    case 'xl':
      h = 44;
      w = 44;
      break;
    default:
      h = 24;
      w = 24;
      break;
  }

  // Color-blind mode: always use solid icons (no halo, no pulsing)
  const useSolid = isColorBlindModeEnabled();

  let icon;
  // DEV-966. Completion no longer collapses to a circle. The trend
  // shape (▲ ● ▼) reflects the engine-computed direction regardless
  // of completion state; the BLUE color (set in defineIndicatorColor)
  // is the sole completion signal. This preserves information about
  // how the project ended up — a completed project that was trending
  // up at the moment of completion still shows ▲, just blue. Was:
  // `if (completed || trend === 0)`.
  if (trend === 0) {
    icon = useSolid
      ? <CircleIcon color={color} height={h} width={w} />
      : <DuotoneCircleIcon color={color} height={h} width={w} />;
  } else if (trend === -1) {
    icon = useSolid
      ? <ArrowDownIcon color={color} height={h} width={w} />
      : <DuotoneArrowDownIcon color={color} height={h} width={w} />;
  } else if (trend === 1) {
    icon = useSolid
      ? <ArrowUpIcon color={color} height={h} width={w} />
      : <DuotoneArrowUpIcon color={color} height={h} width={w} />;
  } else {
    // Fallback: unknown or missing trend — show a circle (no-data / neutral state)
    icon = useSolid
      ? <CircleIcon color={color} height={h} width={w} />
      : <DuotoneCircleIcon color={color} height={h} width={w} />;
  }

  // Wrap in a pulsing HTML span — animates scale + drop-shadow on an HTML
  // element, which is reliable across all browsers (unlike SVG-internal transforms).
  if (pulsing) {
    return <span className="kpi-pulse-wrapper" style={{ color }}>{icon}</span>;
  }
  return icon;
}

const getCompletedIndicator = () => {
  const palette = getActiveIndicatorPalette();
  return <CircleIcon color={palette.completed} height={10} width={10} />
}

/**
 * Render a KPI indicator icon for a given KPI type and data item.
 *
 * @param pulsing — when true, the duotone halo variant will animate to
 *                  signal a recent change. Callers (e.g. KpiGridTable)
 *                  determine whether to pulse by comparing the change
 *                  highlight time window against the row's last-updated
 *                  timestamp.
 */
const getKpiIndicator = (type, item, size = 'md', pulsing = false) => {
  // Defensive: group header rows (and some intermediate rows) don't have the same shape as project rows.
  // We never want KPI rendering to crash the app.
  if (!item) return null;

  let colorValue;
  let trendValue;
  let cellIsCompleted = false;

  // DEV-966. Completion now applies to all five KPIs, not just Summary.
  // When a project's project_status is 'completed', every KPI cell (Summary,
  // Schedule, Finance, Mission, Data Reliability) renders in BLUE with its
  // natural trend shape and engine score preserved. Was: only Summary
  // checked status here; Data used its own _isCompletedProject flag via
  // getDataReliabilityDisplayState; Schedule/Finance/Mission had no
  // completion treatment at all (rendered green/yellow/red on a finished
  // project, which lost the "this is locked, final" signal).
  const status = (item?.project?.project_status ?? '') as any;
  const isProjectCompleted =
    typeof status === 'string' &&
    status.toLowerCase() === 'completed';

  switch (type) {
    case FIELD_TYPES.DATA: {
      // DEV-851: Route through helper to honor the blue Complete-state
      // override. Helper reads `_isCompletedProject` from the row (set
      // by App.tsx) and returns isComplete=true for closed projects.
      const display = getDataReliabilityDisplayState(item);
      colorValue = display.color;
      trendValue = display.trend;
      cellIsCompleted = !!display.isComplete;
      break;
    }
    case FIELD_TYPES.MISSION: {
      // DEV-838: Mission color is subject to the HSE floor cap. Route
      // through the helper — when item.hse_bundle is absent, this returns
      // engine color unchanged, so behavior is identical for non-HSE
      // contexts (i.e. the existing KpiGridTable today).
      const display = getMissionDisplayState(item);
      colorValue = display.color;
      trendValue = display.trend;
      break;
    }
    case FIELD_TYPES.SUMMARY: {
      // DEV-838: Summary color is also capped per spec § 10. Same helper.
      const display = getSummaryDisplayState(item);
      colorValue = display.color;
      trendValue = display.trend;
      break;
    }
    case FIELD_TYPES.FINANCE:
    case FIELD_TYPES.SCHEDULE:
      colorValue = item[`${type}_indicator_color`];
      trendValue = item[`${type}_indicator_trend`];
      break;
  }
  // DEV-966. cellIsCompleted is still honored (set by getDataReliabilityDisplayState
  // when _isCompletedProject is true, which covers cases like soft-closed or
  // archived projects that may not have project_status='completed' but should
  // still render blue). isProjectCompleted is the broader, all-KPIs check.
  const completedFlag = isProjectCompleted || cellIsCompleted;
  const color = defineIndicatorColor(colorValue, completedFlag);
  return defineIndicatorIcon(color, trendValue, completedFlag, size, pulsing);
}

const getDateProgressPercentage = (start, end) => {
  if (!start || !end) return 0;

  const startStamp = Date.parse(start);
  const endStamp = Date.parse(end);
  const now = Date.now();

  if (isNaN(startStamp) || isNaN(endStamp) || endStamp < startStamp )  {
    console.warn('[Schedule] Invalid date range for progress calculation:', { start, end });
    return 0;
  }
  if (startStamp >= now) return 0;
  if (endStamp <= now) return 100;

  const currentInterval = now - startStamp;
  const totalInterval = endStamp - startStamp;

  return Math.round(( currentInterval / totalInterval ) * 100);
}

export { getCompletedIndicator, getKpiIndicator, getDateProgressPercentage }
